import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  DASHBOARD_CHART_COLORS,
  getDashboardChartColor,
  tabPanelSx,
} from '../constants/supplierLedgerUiConstants';
import {
  formatCompactAmount,
} from '../utils/supplierLedgerHelpers';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import useSupplierDashboardData from '../hooks/useSupplierDashboardData';
import {
  DashboardChartCard,
  DashboardMetric,
} from './dashboard/DashboardPrimitives';

const truncateChartLabel = (value, maxLength = 20) => {
  const label = String(value || '-');
  return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
};

const DashboardTab = () => {
  const theme = useTheme();
  const {
    activeInvoices,
    areaData,
    averageInvoice,
    classificationData,
    generatedOrders,
    loading,
    monthlyData,
    paidRatio,
    pendingProviders,
    topConceptData,
    topProviderData,
    totals,
  } = useSupplierDashboardData();
  const chartColors = DASHBOARD_CHART_COLORS;

  return (
    <Box sx={{ ...tabPanelSx, display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, backgroundColor: 'background.paper' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
          gap: 1.25,
        }}
      >
        <DashboardMetric label="Facturado" value={formatAmount(totals.invoiced)} helper={`${activeInvoices.length} facturas`} />
        <DashboardMetric label="Pagado" value={formatAmount(totals.paid)} helper={`${paidRatio.toLocaleString('es-AR', { maximumFractionDigits: 1 })}% del facturado`} />
        <DashboardMetric label="Saldo" value={formatAmount(totals.balance)} helper={`${pendingProviders} proveedores con saldo`} />
        <DashboardMetric label="Ticket promedio" value={formatAmount(averageInvoice)} helper="Por factura activa" />
        <DashboardMetric label="OP generadas" value={generatedOrders.toLocaleString('es-AR')} helper="Facturas reservadas" />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        <DashboardChartCard title="Facturas por mes" loading={loading} hasData={monthlyData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => Number(value || 0).toLocaleString('es-AR')} />
              <Line type="monotone" dataKey="facturas" name="Facturas" stroke={chartColors[2]} strokeWidth={3} dot={{ r: 3, fill: chartColors[2] }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Facturado y pendiente mensual" loading={loading} hasData={monthlyData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Legend />
              <Bar dataKey="facturado" name="Facturado" fill={chartColors[0]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="pendiente" name="Pendiente" fill={chartColors[1]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Proveedores con mayor saldo" loading={loading} hasData={topProviderData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topProviderData} layout="vertical" margin={{ top: 8, right: 20, left: 70, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis type="number" tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={130} tickFormatter={(value) => truncateChartLabel(value, 18)} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Bar dataKey="value" name="Saldo" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {topProviderData.map((entry, index) => (
                  <Cell key={entry.name} fill={getDashboardChartColor(index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Conceptos con más facturado" loading={loading} hasData={topConceptData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topConceptData} layout="vertical" margin={{ top: 8, right: 20, left: 70, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis type="number" tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={130} tickFormatter={(value) => truncateChartLabel(value, 18)} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Bar dataKey="value" name="Facturado" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {topConceptData.map((entry, index) => (
                  <Cell key={entry.name} fill={getDashboardChartColor(index + 2)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Clasificación por saldo pendiente" loading={loading} hasData={classificationData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={classificationData} layout="vertical" margin={{ top: 8, right: 20, left: 95, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis type="number" tickFormatter={formatCompactAmount} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <YAxis type="category" dataKey="name" width={155} tickFormatter={(value) => truncateChartLabel(value, 22)} tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Legend />
              <Bar dataKey="total" name="Total" fill={chartColors[2]} radius={[0, 4, 4, 0]} isAnimationActive={false} />
              <Bar dataKey="pendiente" name="Pendiente" fill={chartColors[1]} radius={[0, 4, 4, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartCard>

        <DashboardChartCard title="Distribución por área" loading={loading} hasData={areaData.length > 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={areaData} dataKey="value" nameKey="name" outerRadius={92} innerRadius={44} paddingAngle={2} isAnimationActive={false}>
                {areaData.map((entry, index) => (
                  <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => formatAmount(value)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </DashboardChartCard>
      </Box>
    </Box>
  );
};

export default DashboardTab;
